import { useEffect, useRef, useState } from "react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { api, fmtPrice } from "@/lib/api";

const MAX_POINTS = 80;
const POLL_MS = 2500;

function seedHistory(currentPrice, symbol) {
  // Synthesize 40 backward-walked points for an immediately interesting chart.
  const vol = symbol === "USD/JPY" ? 0.01 :
              symbol === "XAU/USD" ? 0.05 : 0.0001;
  const out = [];
  let p = currentPrice;
  const now = Date.now();
  for (let i = 39; i >= 0; i--) {
    out.push({ t: now - i * POLL_MS, price: p });
    p += (Math.random() - 0.5) * vol * 1.2;
  }
  // Reverse-walk so end == currentPrice (otherwise lose visual continuity)
  // We'll just leave it as a random preamble; final live tick will appear naturally.
  return out;
}

export default function PriceChart({ symbol, height = 240, compact = false }) {
  const [data, setData] = useState([]);
  const [quote, setQuote] = useState(null);
  const seeded = useRef(false);

  useEffect(() => {
    let mounted = true;
    let lastSymbol = symbol;
    setData([]);
    seeded.current = false;

    const tick = async () => {
      try {
        if (!seeded.current) {
          const [{ data: hist }, { data: q }] = await Promise.all([
            api.get(`/market/history/${encodeURIComponent(symbol)}`, { params: { limit: 120 } }),
            api.get(`/market/rates/${encodeURIComponent(symbol)}`),
          ]);
          if (!mounted || lastSymbol !== symbol) return;
          seeded.current = true;
          setQuote(q);
          const points = (hist.ticks || []).map((t) => ({ t: t.t, price: t.price }));
          points.push({ t: Date.now(), price: q.mid });
          const trimmed = points.length > MAX_POINTS ? points.slice(points.length - MAX_POINTS) : points;
          setData(trimmed);
        } else {
          const { data: q } = await api.get(`/market/rates/${encodeURIComponent(symbol)}`);
          if (!mounted || lastSymbol !== symbol) return;
          setQuote(q);
          setData((prev) => {
            const next = [...prev, { t: Date.now(), price: q.mid }];
            if (next.length > MAX_POINTS) return next.slice(next.length - MAX_POINTS);
            return next;
          });
        }
      } catch {
        /* ignore */
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      mounted = false;
      lastSymbol = null;
      clearInterval(id);
    };
  }, [symbol]);

  if (data.length === 0) {
    return (
      <div className="border border-subtle bg-card flex items-center justify-center text-xs text-muted-foreground" style={{ height }}>
        Loading chart…
      </div>
    );
  }

  const prices = data.map((d) => d.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const pad = (max - min) * 0.15 || max * 0.0005;
  const domain = [min - pad, max + pad];
  const up = quote ? quote.change >= 0 : true;
  const stroke = up ? "#16a34a" : "#dc2626";
  const fillId = `chartGrad-${symbol.replace("/", "-")}`;

  return (
    <div className="bg-card border border-subtle" data-testid={`price-chart-${symbol.replace("/", "-")}`}>
      {!compact && quote && (
        <div className="px-4 py-3 border-b border-subtle flex items-center justify-between">
          <div>
            <div className="label-eyebrow">{symbol}</div>
            <div className="font-mono-num text-2xl font-semibold mt-0.5">{fmtPrice(symbol, quote.mid)}</div>
          </div>
          <div className="text-right">
            <div className={`font-mono-num text-sm font-semibold ${up ? "text-profit" : "text-loss"}`}>
              {up ? "+" : ""}{quote.change_pct.toFixed(3)}%
            </div>
            <div className="text-xs text-muted-foreground font-mono-num mt-0.5">
              {up ? "▲" : "▼"} {fmtPrice(symbol, Math.abs(quote.change))}
            </div>
          </div>
        </div>
      )}
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={stroke} stopOpacity={0.35} />
                <stop offset="100%" stopColor={stroke} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="t" hide />
            <YAxis
              domain={domain}
              orientation="right"
              tick={{ fill: "#a1a1aa", fontSize: 10, fontFamily: "IBM Plex Mono" }}
              tickFormatter={(v) => fmtPrice(symbol, v)}
              width={70}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                background: "#121214",
                border: "1px solid #27272a",
                borderRadius: 4,
                fontFamily: "IBM Plex Mono",
                fontSize: 12,
              }}
              labelStyle={{ color: "#a1a1aa" }}
              labelFormatter={(v) => new Date(v).toLocaleTimeString()}
              formatter={(v) => [fmtPrice(symbol, v), "Price"]}
            />
            {quote && (
              <ReferenceLine
                y={quote.mid}
                stroke={stroke}
                strokeDasharray="2 4"
                strokeOpacity={0.5}
              />
            )}
            <Area
              type="monotone"
              dataKey="price"
              stroke={stroke}
              strokeWidth={1.75}
              fill={`url(#${fillId})`}
              isAnimationActive={false}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
